import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Checkbox,
    Chip,
    Breadcrumbs,
    Link as MuiLink,
    CircularProgress,
    Snackbar,
    Alert
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { getRiders, createRunSheet, Rider } from '../../services/runSheetApi';
import { getAvailableMasterUnits, MasterUnit } from '../../services/runSheetApi';

const RunSheetCreate = () => {
    const { theme } = useTheme();
    const navigate = useNavigate();
    const [riders, setRiders] = useState<Rider[]>([]);
    const [masterUnits, setMasterUnits] = useState<MasterUnit[]>([]);
    const [selectedRider, setSelectedRider] = useState<number | ''>('');
    const [selectedMasterUnits, setSelectedMasterUnits] = useState<number[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [loading, setLoading] = useState(true);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'warning' | 'info' });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [ridersRes, masterUnitsRes] = await Promise.all([
                getRiders(),
                getAvailableMasterUnits()
            ]);
            setRiders(ridersRes.data || []);
            // API returns paginated data, so we need to access .data.data
            setMasterUnits(masterUnitsRes.data?.data || []);
        } catch (error: any) {
            console.error('Error fetching data:', error);
            // Ensure arrays are set even on error
            setRiders([]);
            setMasterUnits([]);
            setSnackbar({ 
                open: true, 
                message: 'Failed to load data. Please try again.', 
                severity: 'error' 
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSelectAll = (checked: boolean) => {
        if (checked) {
            setSelectedMasterUnits(masterUnits.map(mu => mu.id));
        } else {
            setSelectedMasterUnits([]);
        }
    };

    const handleSelectMasterUnit = (id: number, checked: boolean) => {
        if (checked) {
            setSelectedMasterUnits([...selectedMasterUnits, id]);
        } else {
            setSelectedMasterUnits(selectedMasterUnits.filter(muId => muId !== id));
        }
    };

    const handleSubmit = async () => {
        if (!selectedRider) {
            setSnackbar({ open: true, message: 'Please select a rider', severity: 'warning' });
            return;
        }

        if (selectedMasterUnits.length === 0) {
            setSnackbar({ open: true, message: 'Please select at least one master unit', severity: 'warning' });
            return;
        }

        try {
            setSubmitting(true);
            await createRunSheet({
                rider_id: selectedRider as number,
                masterunit_ids: selectedMasterUnits
            });
            setSnackbar({ open: true, message: 'Run sheet created successfully!', severity: 'success' });
            setTimeout(() => navigate('/run-sheets'), 1500);
        } catch (error: any) {
            setSnackbar({ open: true, message: error.message || 'Failed to create run sheet', severity: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Breadcrumbs sx={{ mb: 3 }}>
                <MuiLink
                    component="button"
                    variant="body1"
                    onClick={() => navigate('/dashboard')}
                    sx={{ textDecoration: 'none', color: theme.palette.text.primary }}
                >
                    Home
                </MuiLink>
                <MuiLink
                    component="button"
                    variant="body1"
                    onClick={() => navigate('/run-sheets')}
                    sx={{ textDecoration: 'none', color: theme.palette.text.primary }}
                >
                    Run Sheets
                </MuiLink>
                <Typography color="text.primary">New</Typography>
            </Breadcrumbs>

            <Card>
                <CardContent>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                        Create Run Sheet
                    </Typography>

                    {/* Basic Information */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                            Basic Information
                        </Typography>
                        <FormControl fullWidth sx={{ maxWidth: 400 }}>
                            <InputLabel>Rider *</InputLabel>
                            <Select
                                value={selectedRider}
                                onChange={(e) => setSelectedRider(e.target.value as number)}
                                label="Rider *"
                            >
                                <MenuItem value="">Select Rider</MenuItem>
                                {riders.map((rider) => (
                                    <MenuItem key={rider.id} value={rider.id}>
                                        {rider.name} ({rider.loginID})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    {/* Master Units Table */}
                    <Typography variant="h6" gutterBottom>
                        Available Master Units ({selectedMasterUnits.length} selected)
                    </Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={masterUnits.length > 0 && selectedMasterUnits.length === masterUnits.length}
                                            indeterminate={selectedMasterUnits.length > 0 && selectedMasterUnits.length < masterUnits.length}
                                            onChange={(e) => handleSelectAll(e.target.checked)}
                                        />
                                    </TableCell>
                                    <TableCell>Master Unit #</TableCell>
                                    <TableCell>Consignments</TableCell>
                                    <TableCell>Client Name</TableCell>
                                    <TableCell>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {masterUnits.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center">No master units available</TableCell>
                                    </TableRow>
                                ) : (
                                    masterUnits.map((mu) => (
                                        <TableRow key={mu.id}>
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    checked={selectedMasterUnits.includes(mu.id)}
                                                    onChange={(e) => handleSelectMasterUnit(mu.id, e.target.checked)}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <MuiLink
                                                    component="button"
                                                    onClick={() => navigate(`/master-units/${mu.id}`)}
                                                    sx={{ textDecoration: 'none' }}
                                                >
                                                    {mu.mu_number}
                                                </MuiLink>
                                            </TableCell>
                                            <TableCell>{mu.consignments?.length || 0}</TableCell>
                                            <TableCell>{mu.client?.name || '-'}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={mu.status.name}
                                                    color="info"
                                                    size="small"
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                        <Button
                            variant="outlined"
                            onClick={() => navigate('/run-sheets')}
                            sx={{ mr: 2 }}
                            disabled={submitting}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSubmit}
                            disabled={submitting || selectedMasterUnits.length === 0 || !selectedRider}
                        >
                            {submitting ? 'Creating...' : 'Submit'}
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default RunSheetCreate;



